import { Component, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbModal,
  NgbTooltip
} from "@ng-bootstrap/ng-bootstrap";
import { BsbPipe } from "@shared/pipes/bsb.pipe";
import { ToastrService } from "ngx-toastr";
import { delay, from, of, take, tap } from "rxjs";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import {
  AutoPayAgreementModalComponent
} from "./components/auto-pay-agreement-modal/auto-pay-agreement-modal.component";
import { AutoPayStatus } from './constants/auto-pay-status';

export function getLoadingTimeMs(min = 500, max = 3000): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export class AutoPayAgreementDto {
  status: AutoPayStatus;
  entityId: string;
  contactId: string;
  bsb: string;
  bankAccountDetails: string;
  accountName: string;

  constructor(data: Partial<AutoPayAgreementDto> = {}) {
    this.status = data.status ?? AutoPayStatus.NotFilled;
    this.entityId = data.entityId ?? '';
    this.contactId = data.contactId ?? '';
    this.bsb = data.bsb ?? '';
    this.bankAccountDetails = data.bankAccountDetails?.toString() ?? '';
    this.accountName = data.accountName ?? '';
  }
}

@Component({
  selector: 'app-auto-pay',
  standalone: true,
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgbTooltip,
    BsbPipe
  ],
  templateUrl: 'auto-pay.component.html',
  styleUrl: 'auto-pay.component.scss'
})
export class AutoPayComponent implements OnInit {
  modalService = inject(NgbModal);
  toastr = inject(ToastrService);

  @Input({ required: true }) entityId!: string;
  @Input({ required: true }) entityName!: string;

  readonly AutoPayStatus = AutoPayStatus;
  modalOpened = false;

  autoPayData = new AutoPayAgreementDto();

  ngOnInit(): void {
    const autoPayData = this.getEntityAutoPayData(this.entityId);
    if (autoPayData) {
      this.autoPayData = autoPayData;
    }
  }

  openAddBankDataModal(editData: AutoPayAgreementDto | null = null): void {
    const modalRef = this.modalService.open(
      AutoPayAgreementModalComponent,
      {
        size: 'sm',
        modalDialogClass: 'modal-sm-plus',
      },
    );
    const instance = modalRef.componentInstance as AutoPayAgreementModalComponent;

    instance.entityId = this.entityId;
    instance.editData = editData;

    if (!editData) {
      instance.form.controls.accountName.setValue(this.entityName);
    }

    this.modalOpened = true;

    from(modalRef.result)
      .pipe(take(1), delay(getLoadingTimeMs()))
      .subscribe({
        next: (entityAutoPayData: AutoPayAgreementDto) => {
          if (this.autoPayData.status == AutoPayStatus.NotFilled) {
            entityAutoPayData.status = AutoPayStatus.PendingSignature;
            this.autoPayData.status = AutoPayStatus.PendingSignature;
          }

          this.saveEntityAutoPayData(entityAutoPayData);
          this.modalOpened = false;
          this.toastr.success('New agreement was sent for signing.');

          setTimeout(() => {
            if (this.autoPayData.status == AutoPayStatus.PendingSignature) {
              entityAutoPayData.status = AutoPayStatus.TurnedOn;
              this.autoPayData.status = AutoPayStatus.TurnedOn;
              this.saveEntityAutoPayData(entityAutoPayData);
              this.autoPayData = entityAutoPayData;
            }
          }, 5_000);
        },
        error: () => {
          this.modalOpened = false;
        }
      });
  }

  voidEmail(): void {
    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;
    const instanceDestroyRef = instance.getDestroyRef();

    const body = `<div class="p-2 text-center">Are you sure you want to void the envelope?</div>`;
    const actions: IActionButtonConfig[] = [
      {
        text: 'Yes',
        action: of(null)
          .pipe(
            takeUntilDestroyed(instanceDestroyRef),
            delay(getLoadingTimeMs()),
            tap(() => {
              instance.close();
              this.autoPayData = new AutoPayAgreementDto();
              this.deleteEntityAutoPayData();
            })
          )
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(null)
          .pipe(
            takeUntilDestroyed(instanceDestroyRef),
            tap(() => instance.close())
          )
      },
    ];

    instance.config = {
      ...instance.config,
      header: 'Void Envelope',
      iconClass: 'ic-info-circle-20',
      body,
      actions,
    };
  }

  toggleAutopay(status: AutoPayStatus): void {
    if (status === AutoPayStatus.TurnedOff) {
      this.autoPayData.status = AutoPayStatus.TurnedOn;
      this.toastr.success('Autopay is on.');
    } else if (status === AutoPayStatus.TurnedOn) {
      this.autoPayData.status = AutoPayStatus.TurnedOff;
      this.toastr.success('Autopay is off.');
    }
  }

  editAndSendNewDDA(): void {
    const autoPayData = this.getEntityAutoPayData(this.entityId);
    this.openAddBankDataModal(autoPayData);
  }

  deleteAutoPayData(): void {
    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;
    const instanceDestroyRef = instance.getDestroyRef();

    const body = `<div class="p-2  text-center">By deleting your payment details, Autopay will be disconnected, and a new agreement will be required to reconnect. Are you sure you want to proceed?</div>`;
    const actions: IActionButtonConfig[] = [
      {
        text: 'Yes',
        action: of(null)
          .pipe(
            takeUntilDestroyed(instanceDestroyRef),
            delay(getLoadingTimeMs()),
            tap(() => {
              this.deleteEntityAutoPayData();
              this.autoPayData = new AutoPayAgreementDto();
              instance.close();
              this.toastr.success('Payment details were deleted.');
            })
          )
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(null)
          .pipe(
            takeUntilDestroyed(instanceDestroyRef),
            tap(() => instance.close())
          )
      },
    ];

    instance.config = {
      ...instance.config,
      header: 'Delete Payment Details',
      iconClass: 'ic-info-circle-20',
      body,
      actions,
    };
  }


  // demo purpose only
  readonly autopayDataKey = 'autopay';

  saveEntityAutoPayData(autopayData: AutoPayAgreementDto): void {
    const autopay = this.getAllEntityAutoPayData();
    autopay[autopayData.entityId] = autopayData;
    localStorage.setItem(this.autopayDataKey, JSON.stringify(autopay));
  }

  deleteEntityAutoPayData(entityId: string = this.entityId): void {
    const autopay = this.getAllEntityAutoPayData();

    if (autopay[entityId]) {
      delete autopay[entityId];
    }

    localStorage.setItem(this.autopayDataKey, JSON.stringify(autopay));
  }

  getEntityAutoPayData(entityId: string): AutoPayAgreementDto | null {
    const autopay = this.getAllEntityAutoPayData();

    if (autopay[entityId]) {
      return new AutoPayAgreementDto(autopay[entityId]);
    }

    return null;
  }

  getAllEntityAutoPayData(): Record<string, AutoPayAgreementDto> {
    const stringData = localStorage.getItem(this.autopayDataKey);
    return stringData
      ? JSON.parse(stringData) as Record<string, AutoPayAgreementDto>
      : {};
  }

  get accountName(): string {
    return [
      this.autoPayData.bankAccountDetails.substring(0, 2),
      '*'.repeat(this.autoPayData.bankAccountDetails.length - 4),
      this.autoPayData.bankAccountDetails.substring(this.autoPayData.bankAccountDetails.length - 2),
    ]
      .join('');
  }
}
